import { useState, useEffect } from 'react'
import api from '../../utils/api'
import Button from '../ui/Button'
import Alert from '../ui/Alert'
import { UserPlus, Shield, MapPin, Clock, Eye, EyeOff, ChevronDown, ChevronUp, X } from 'lucide-react'

// ─── Role definitions ─────────────────────────────────────────────────────────
const ROLES = [
  { value: 'admin',            label: 'مدير النظام',          color: 'bg-red-100 text-red-700',       desc: 'وصول كامل لكل شيء' },
  { value: 'reviewer',         label: 'مراجع',                color: 'bg-blue-100 text-blue-700',     desc: 'يراجع ويغيّر حالة الطلبات' },
  { value: 'readonly',         label: 'قراءة فقط',            color: 'bg-stone-100 text-stone-600',   desc: 'يشاهد الطلبات دون تعديل' },
  { value: 'organization',     label: 'جهة توثيقية',          color: 'bg-amber-100 text-amber-700',   desc: 'يرفع ملفات Excel جماعية' },
  { value: 'legal_org',        label: 'جهة قانونية',          color: 'bg-purple-100 text-purple-700', desc: 'يشاهد الحالات ويصدّر للمراجعة القانونية' },
  { value: 'field_researcher', label: 'باحث ميداني',          color: 'bg-green-100 text-green-700',   desc: 'باحث في مناطق محددة' },
]

// ─── Default permissions per role (mirrors backend) ──────────────────────────
const ROLE_DEFAULTS = {
  admin:            { can_bulk_import: true,  can_edit_fields: true,  can_change_status: true,  can_export: true,  can_view_pii: true,  can_view_evidence: true  },
  reviewer:         { can_bulk_import: false, can_edit_fields: true,  can_change_status: true,  can_export: true,  can_view_pii: true,  can_view_evidence: true  },
  readonly:         { can_bulk_import: false, can_edit_fields: false, can_change_status: false, can_export: false, can_view_pii: false, can_view_evidence: false },
  organization:     { can_bulk_import: true,  can_edit_fields: false, can_change_status: false, can_export: false, can_view_pii: false, can_view_evidence: false },
  legal_org:        { can_bulk_import: false, can_edit_fields: false, can_change_status: false, can_export: true,  can_view_pii: true,  can_view_evidence: true  },
  field_researcher: { can_bulk_import: false, can_edit_fields: false, can_change_status: false, can_export: false, can_view_pii: false, can_view_evidence: false },
}

const PERM_LABELS = {
  can_bulk_import:   'استيراد جماعي',
  can_edit_fields:   'تعديل الحقول',
  can_change_status: 'تغيير الحالة',
  can_export:        'تصدير البيانات',
  can_view_pii:      'بيانات شخصية (الهاتف)',
  can_view_evidence: 'مشاهدة المستندات',
}

const EMPTY_FORM = {
  username: '', password: '', display_name: '', role: 'organization',
  assigned_regions: [], assigned_neighborhoods: [],
  permissions: {}, access_expires_at: '',
}

// ─── Small helpers ────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`flex-shrink-0 w-10 h-5 rounded-full transition-colors ${value ? 'bg-primary-600' : 'bg-stone-300'}`}
      >
        <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform mx-0.5 ${value ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
      <span className="text-sm text-stone-700">{label}</span>
    </label>
  )
}

// ─── User form (create / edit) ────────────────────────────────────────────────
function UserForm({ initial = EMPTY_FORM, onSave, onCancel, saving, error, regions }) {
  const [form, setForm] = useState(initial)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const defaults = ROLE_DEFAULTS[form.role] || {}

  function set(key, value) { setForm(f => ({ ...f, [key]: value })) }

  function toggleRegion(id) {
    const cur = form.assigned_regions
    set('assigned_regions', cur.includes(id) ? cur.filter(r => r !== id) : [...cur, id])
    // Clear neighborhoods that belong to removed region
    if (cur.includes(id)) {
      const region = regions.find(r => r.id === id)
      if (region) {
        const nbIds = (region.neighborhoods || []).map(n => n.id)
        set('assigned_neighborhoods', form.assigned_neighborhoods.filter(n => !nbIds.includes(n)))
      }
    }
  }

  function toggleNeighborhood(id) {
    const cur = form.assigned_neighborhoods
    set('assigned_neighborhoods', cur.includes(id) ? cur.filter(n => n !== id) : [...cur, id])
  }

  function setPermOverride(key, value) {
    // Only store overrides that differ from role default
    const newPerms = { ...form.permissions }
    if (value === defaults[key]) {
      delete newPerms[key] // remove override if matches default
    } else {
      newPerms[key] = value
    }
    set('permissions', newPerms)
  }

  function getEffective(key) {
    return key in form.permissions ? form.permissions[key] : defaults[key]
  }

  // Visible neighborhoods: only those whose parent region is selected
  const visibleNeighborhoods = regions
    .filter(r => form.assigned_regions.includes(r.id))
    .flatMap(r => (r.neighborhoods || []).map(n => ({ ...n, regionName: r.name })))

  const isEdit = !!initial.id

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-4">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-stone-800 flex items-center gap-2">
          <UserPlus size={16} />
          {isEdit ? 'تعديل الحساب' : 'حساب جديد'}
        </h2>
        <button onClick={onCancel} className="text-stone-400 hover:text-stone-700"><X size={18} /></button>
      </div>

      {error && <Alert type="error" message={error} className="mb-4" />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        {!isEdit && (
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1 uppercase tracking-wide">اسم المستخدم *</label>
            <input
              type="text" value={form.username}
              onChange={e => set('username', e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              required
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1 uppercase tracking-wide">الاسم المعروض</label>
          <input
            type="text" value={form.display_name}
            onChange={e => set('display_name', e.target.value)}
            placeholder="مثال: جمعية شهداء دمشق"
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1 uppercase tracking-wide">
            {isEdit ? 'كلمة مرور جديدة (اتركها فارغة للإبقاء)' : 'كلمة المرور *'}
          </label>
          <input
            type="password" value={form.password}
            onChange={e => set('password', e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            minLength={isEdit ? undefined : 8}
            required={!isEdit}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1 uppercase tracking-wide">الدور</label>
          <select
            value={form.role}
            onChange={e => { set('role', e.target.value); set('permissions', {}) }}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            {ROLES.map(r => (
              <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1 uppercase tracking-wide flex items-center gap-1">
            <Clock size={12} /> انتهاء الوصول (اختياري)
          </label>
          <input
            type="datetime-local" value={form.access_expires_at}
            onChange={e => set('access_expires_at', e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <p className="text-xs text-stone-400 mt-0.5">للحسابات المؤقتة (صحفيين، فرق قانونية)</p>
        </div>
      </div>

      {/* Region assignment */}
      {form.role !== 'admin' && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={14} className="text-primary-600" />
            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">المناطق المسموحة</span>
            {form.assigned_regions.length === 0 && (
              <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">لا توجد مناطق = لا وصول للحالات</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {regions.map(r => (
              <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                form.assigned_regions.includes(r.id)
                  ? 'bg-primary-50 border-primary-300 text-primary-800'
                  : 'border-stone-200 hover:bg-stone-50'
              }`}>
                <input
                  type="checkbox"
                  checked={form.assigned_regions.includes(r.id)}
                  onChange={() => toggleRegion(r.id)}
                  className="accent-primary-600"
                />
                {r.name}
              </label>
            ))}
          </div>

          {/* Neighborhoods — only when regions are selected */}
          {visibleNeighborhoods.length > 0 && (
            <div>
              <p className="text-xs text-stone-500 mb-2">
                تقييد إضافي للأحياء (اتركه فارغاً للسماح بكامل المنطقة)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto pr-1">
                {visibleNeighborhoods.map(n => (
                  <label key={n.id} className={`flex items-center gap-1.5 p-1.5 rounded-lg border cursor-pointer text-xs transition-colors ${
                    form.assigned_neighborhoods.includes(n.id)
                      ? 'bg-amber-50 border-amber-300 text-amber-800'
                      : 'border-stone-100 hover:bg-stone-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={form.assigned_neighborhoods.includes(n.id)}
                      onChange={() => toggleNeighborhood(n.id)}
                      className="accent-amber-600"
                    />
                    <span>{n.name}</span>
                    <span className="text-stone-400">({n.regionName})</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced: permission overrides */}
      <div className="border border-stone-100 rounded-xl overflow-hidden mb-5">
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-stone-600 hover:bg-stone-50"
        >
          <span className="flex items-center gap-2">
            <Shield size={14} />
            الصلاحيات التفصيلية
            {Object.keys(form.permissions).length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                {Object.keys(form.permissions).length} تعديل
              </span>
            )}
          </span>
          {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showAdvanced && (
          <div className="px-4 pb-4 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-stone-50">
            <p className="sm:col-span-2 text-xs text-stone-400">
              الافتراضي للدور موضّح تلقائياً. فعّل/عطّل لتجاوزه على هذا الحساب تحديداً.
            </p>
            {Object.entries(PERM_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-stone-700">{label}</span>
                <div className="flex items-center gap-2">
                  {key in form.permissions && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">مخصص</span>
                  )}
                  <Toggle
                    value={getEffective(key)}
                    onChange={v => setPermOverride(key, v)}
                    label=""
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} className="text-sm text-stone-500 hover:text-stone-800 px-4 py-2">
          إلغاء
        </button>
        <Button onClick={() => onSave(form)} loading={saving}>
          {isEdit ? 'حفظ التعديلات' : 'إنشاء الحساب'}
        </Button>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UsersPanel() {
  const [users,   setUsers]   = useState([])
  const [regions, setRegions] = useState([])
  const [loading, setLoading] = useState(true)

  const [showCreate, setShowCreate] = useState(false)
  const [editUser,   setEditUser]   = useState(null)
  const [saving,     setSaving]     = useState(false)
  const [formError,  setFormError]  = useState('')

  useEffect(() => {
    Promise.all([
      api.get('admin/users').then(r => r.data),
      api.get('admin/regions').then(r => r.data),
    ]).then(([u, r]) => { setUsers(u); setRegions(r) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate(form) {
    setFormError('')
    if (form.password.length < 8) { setFormError('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return }
    setSaving(true)
    try {
      const payload = {
        username: form.username, password: form.password,
        display_name: form.display_name, role: form.role,
        assigned_regions: form.assigned_regions,
        assigned_neighborhoods: form.assigned_neighborhoods,
        permissions: form.permissions,
        access_expires_at: form.access_expires_at || null,
      }
      const { data } = await api.post('admin/users', payload)
      setUsers(prev => [data, ...prev])
      setShowCreate(false)
    } catch (err) {
      setFormError(err.message || 'فشل إنشاء الحساب')
    } finally { setSaving(false) }
  }

  async function handleEdit(form) {
    setFormError('')
    setSaving(true)
    try {
      const payload = {
        display_name: form.display_name, role: form.role,
        assigned_regions: form.assigned_regions,
        assigned_neighborhoods: form.assigned_neighborhoods,
        permissions: form.permissions,
        access_expires_at: form.access_expires_at || null,
      }
      if (form.password) payload.password = form.password
      const { data } = await api.patch(`admin/users/${editUser.id}`, payload)
      setUsers(prev => prev.map(u => u.id === data.id ? { ...u, ...data } : u))
      setEditUser(null)
    } catch (err) {
      setFormError(err.message || 'فشل حفظ التعديلات')
    } finally { setSaving(false) }
  }

  async function handleToggleActive(user) {
    try {
      const { data } = await api.patch(`admin/users/${user.id}`, { is_active: !user.is_active })
      setUsers(prev => prev.map(u => u.id === data.id ? { ...u, ...data } : u))
    } catch { /* ignore */ }
  }

  async function handleDelete(user) {
    if (!confirm(`هل تريد حذف حساب "${user.display_name || user.username}" نهائياً؟`)) return
    try {
      await api.delete(`admin/users/${user.id}`)
      setUsers(prev => prev.filter(u => u.id !== user.id))
    } catch (err) { alert(err.message) }
  }

  function openEdit(user) {
    setFormError('')
    setEditUser({
      ...user,
      password: '',
      assigned_regions:       user.assigned_regions       || [],
      assigned_neighborhoods: user.assigned_neighborhoods || [],
      permissions:            user.permissions            || {},
      access_expires_at:      user.access_expires_at
        ? new Date(user.access_expires_at).toISOString().slice(0, 16)
        : '',
    })
  }

  function regionNames(ids) {
    return regions.filter(r => ids?.includes(r.id)).map(r => r.name).join('، ') || null
  }

  function neighborhoodNames(ids) {
    if (!ids?.length) return null
    const all = regions.flatMap(r => r.neighborhoods || [])
    return all.filter(n => ids.includes(n.id)).map(n => n.name).join('، ')
  }

  function isExpired(user) {
    return user.access_expires_at && new Date(user.access_expires_at) < new Date()
  }

  return (
    <div dir="rtl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-stone-800">إدارة الحسابات</h1>
        {!showCreate && !editUser && (
          <Button onClick={() => { setShowCreate(true); setFormError('') }}>
            <UserPlus size={15} className="mr-1" /> حساب جديد
          </Button>
        )}
      </div>

      {showCreate && (
        <UserForm
          regions={regions}
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
          saving={saving}
          error={formError}
        />
      )}

      {editUser && (
        <UserForm
          initial={editUser}
          regions={regions}
          onSave={handleEdit}
          onCancel={() => setEditUser(null)}
          saving={saving}
          error={formError}
        />
      )}

      {loading ? (
        <p className="text-stone-500 text-sm">جارٍ التحميل...</p>
      ) : (
        <div className="space-y-2">
          {users.map(user => {
            const role  = ROLES.find(r => r.value === user.role)
            const rNames = regionNames(user.assigned_regions)
            const nNames = neighborhoodNames(user.assigned_neighborhoods)
            const expired = isExpired(user)
            const overrides = Object.keys(user.permissions || {}).length

            return (
              <div
                key={user.id}
                className={`bg-white border rounded-xl px-4 py-3 ${
                  !user.is_active || expired ? 'opacity-60' : 'border-stone-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-stone-800 text-sm">
                        {user.display_name || user.username}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${role?.color || 'bg-stone-100 text-stone-600'}`}>
                        {role?.label || user.role}
                      </span>
                      {!user.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">معطَّل</span>
                      )}
                      {expired && (
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock size={10} /> منتهي
                        </span>
                      )}
                      {overrides > 0 && (
                        <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Shield size={10} /> {overrides} تعديل
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">@{user.username}</p>

                    {/* Region / neighborhood badges */}
                    {rNames && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span className="text-xs text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <MapPin size={10} /> {rNames}
                        </span>
                        {nNames && (
                          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                            {nNames}
                          </span>
                        )}
                      </div>
                    )}
                    {!rNames && user.role !== 'admin' && (
                      <span className="text-xs text-red-500 mt-1 block">لا توجد مناطق مخصصة</span>
                    )}

                    {/* Expires */}
                    {user.access_expires_at && (
                      <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                        <Clock size={10} />
                        ينتهي: {new Date(user.access_expires_at).toLocaleDateString('ar-SA')}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEdit(user)}
                      className="text-xs px-3 py-1 border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600"
                    >
                      تعديل
                    </button>
                    <button
                      onClick={() => handleToggleActive(user)}
                      className="text-xs px-3 py-1 border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600"
                    >
                      {user.is_active ? 'تعطيل' : 'تفعيل'}
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="text-xs px-3 py-1 border border-red-200 rounded-lg hover:bg-red-50 text-red-600"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
